import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getJson, postJson, PRC_PREFIX } from "./client";

/**
 * Manage Capital — the governed record of how much capital each investor has.
 *
 * ── Why a capital ceiling is not a settings field ───────────────────────────────────────
 * It is the DENOMINATOR every percentage limit is measured against. An internal limit reads
 * "35% of capital"; move the capital and every percentage, headroom and RAG status under it
 * moves with it, without a single limit having been touched. So a ceiling change goes
 * through propose -> approve -> effective-dated -> permanently logged, exactly like a limit
 * change, and this module mirrors limit-config.ts rather than being a lighter version of it.
 *
 * ── Three types, because funding and appetite are different questions ───────────────────
 * This started as Debt vs Equity and that was one value short, which showed up as a real
 * error: Wafra's $2.5B was modelled as Equity, so the Capacity page's "Equity funded" card
 * would have reported $2,885,000,000 of equity against a true figure of $385,000,000.
 *
 *   Debt      FUNDING, held in a book. Wafra: CAP 1 $1.7B, CAP 2 $1.0B.
 *   Equity    FUNDING, not held in a book. Navitas: $385M. Navitas invests equity only and
 *             has no facility, which is why it has no external limits.
 *   Internal  APPETITE. The ceiling WE set to measure an investor's internal limits
 *             against — Wafra $2.5B, Navitas $385M. Not money anyone put in.
 *
 * Only Internal moves a risk number: approving one writes
 * prc_limit_config.total_capital_usd. Raising an investor's Equity no longer re-scores
 * their limits, which it did before sql/81_ split the two.
 *
 * Equity and Internal are singletons PER INVESTOR, not globally. The original spec called
 * equity a global singleton, which cannot hold once two investors each have capital.
 */
export type CapitalType = "Debt" | "Equity" | "Internal";

/** Types not held in a book: they carry no facilityCode and are unique per investor. */
export const UNBOOKED_TYPES: CapitalType[] = ["Equity", "Internal"];

/** The one type that drives the internal limit denominator. */
export const APPETITE_TYPE: CapitalType = "Internal";

/**
 * ── Three FACTS about a source, one governance mechanism ────────────────────────────────
 *
 * A capital source has three definitional properties. They are all "what this source IS",
 * which is why they live here and not in Manage limits — a limit constrains what is allowed
 * INSIDE a pool, these say what the pool is:
 *
 *   CEILING       the committed amount. The denominator under every percentage limit.
 *   ADVANCE_RATE  how much of pledged collateral actually becomes usable capacity. A $1.7B
 *                 facility at an 85% advance rate does not fund $1.7B of book.
 *   CODE_MAP      which raw BankDwn investor codes constitute the pool.
 *
 * Each change is its OWN proposal, approval and log row rather than one blended "changed
 * something" entry: moving a ceiling moves the denominator under every limit, while moving a
 * code mapping changes which contracts are in scope at all. A reviewer needs to know which
 * of those they are approving, and the log needs to say which one happened.
 *
 * The per-source lock keys on (source, kind), so a pending mapping change does not block a
 * ceiling adjustment — they are independent decisions a reviewer may take in either order.
 */
export type CapitalChangeKind = "CEILING" | "ADVANCE_RATE" | "CODE_MAP";

/** How each kind is labelled wherever a proposal is shown. Matches capital.py's copy. */
export const CHANGE_KIND_LABEL: Record<CapitalChangeKind, string> = {
  CEILING: "Ceiling",
  ADVANCE_RATE: "Advance Rate",
  CODE_MAP: "Code Mapping",
};

/**
 * The one type a lender advances against pledged collateral for.
 *
 * Equity is money the investor put in; Internal is a ceiling we chose. Neither has a lender,
 * so an advance rate on either is not an unset value — it is a category error, and the field
 * is ABSENT from those forms rather than empty.
 */
export const ADVANCE_RATE_TYPE: CapitalType = "Debt";

/**
 * Types that are a pool of actual contracts, and therefore have raw investor codes.
 *
 * An internal cap is a number we set, not a set of rows in the warehouse, so it has nothing
 * to map.
 */
export const MAPPABLE_TYPES: CapitalType[] = ["Debt", "Equity"];

/** Wire shape of a registered capital source. */
export interface CapitalSource {
  capitalSourceId: string;
  investor: string;
  /** Null for Equity and Internal — neither is held in a book. */
  facilityCode: string | null;
  facilityName: string | null;
  capitalType: CapitalType;
  ceilingDollars: number;
  /**
   * Percent of pledged collateral this lender advances against — 85 for 85%, NOT 0.85.
   *
   * The opposite convention to `thresholdPct` on a limit, which is a fraction, and the two
   * are a factor of 100 apart in a product where a capacity figure off by 100x would not
   * look obviously wrong. Both the API and the DB CHECK reject a fraction.
   *
   * Null means NO RATE RECORDED, which is not the same as 100% and must not be rendered as
   * it. Debt only — null on every Equity and Internal row by construction.
   */
  advanceRatePct: number | null;
  /**
   * Which raw BankDwn investor codes constitute this pool, as normalised strings ("10").
   *
   * Three states, and the difference between the last two matters:
   *   null   no mapping has ever existed for this source — an omission.
   *   []     a mapping exists and names no codes — a decision someone took and a reviewer
   *          approved.
   *   [...]  the codes.
   */
  investorCodes: string[] | null;
  effectiveStartDate: string;
  /** Null while in force; set to the day before its successor starts. */
  effectiveEndDate: string | null;
  status: "LIVE" | "SUPERSEDED";
  /**
   * ACTIVE limits constraining this source. Zero is what drives the "No limits set"
   * indicator — a source with a ceiling and nothing limiting it is unconstrained, and
   * nothing is stopping deployment concentrating however it happens to land.
   *
   * Counted server-side across both limit tables, and ACTIVE-only: sql/72_ left 31 PENDING
   * external copies in prc_limit_config, and a PENDING row constrains nothing.
   */
  limitCount: number | null;
}

export interface CapitalProposal {
  proposalId: string;
  /** Null on a creation. */
  capitalSourceId: string | null;
  investor: string;
  facilityCode: string | null;
  facilityName: string | null;
  capitalType: CapitalType;
  /** Which fact this proposal changes. 'CEILING' on every row raised before sql/83_. */
  changeKind: CapitalChangeKind;
  /**
   * Whether THIS FACT existed before — not whether the source did.
   *
   * The first advance rate on a facility that has carried a ceiling for a year is an ADD,
   * and the log reads "— → 85%". That reading is the useful one: it says what was decided.
   */
  changeType: "ADD" | "ADJUST";
  /** Null on a creation — the log renders it as "—", which is the point. */
  oldCeilingDollars: number | null;
  /** Null on an ADVANCE_RATE or CODE_MAP proposal: those carry no ceiling at all. */
  newCeilingDollars: number | null;
  /** Populated on an ADVANCE_RATE proposal, and on a CEILING one that CREATED the source. */
  oldAdvanceRatePct: number | null;
  newAdvanceRatePct: number | null;
  /** Populated on a CODE_MAP proposal. Null old codes = the first mapping for this source. */
  oldInvestorCodes: string[] | null;
  newInvestorCodes: string[] | null;
  effectiveStartDate: string;
  proposedByRole: string;
  proposedAt: string;
  reason: string | null;
  status: "PENDING" | "APPROVED" | "REJECTED";
  reviewedByRole: string | null;
  reviewedAt: string | null;
  reviewNotes: string | null;
}

/**
 * One proposal, about one fact. The kind decides which of the three payload fields is
 * required and, just as importantly, which are rejected — the backend's shape CHECK will
 * refuse a CODE_MAP that also carries a ceiling.
 */
export interface CapitalProposalInput {
  investor: string;
  capitalType: CapitalType;
  changeKind: CapitalChangeKind;
  facilityCode?: string | null;
  facilityName?: string | null;
  /** Required for CEILING. Must be absent otherwise. */
  newCeilingDollars?: number | null;
  /**
   * Required for ADVANCE_RATE. Allowed on a CEILING proposal ONLY when it creates the
   * source: a source is born with both amounts, and splitting that in two would leave a
   * newly-approved facility with no rate for however long the second review takes. On an
   * existing source the backend refuses it and asks for a separate proposal.
   */
  newAdvanceRatePct?: number | null;
  /** Required for CODE_MAP. An empty array is legal — "explicitly nothing mapped". */
  investorCodes?: string[] | null;
  effectiveStartDate: string;
  reason: string;
}

/** An investor, from the enumeration prc_investor holds. */
export interface Investor {
  investorCode: string;
  displayName: string;
  /** Whether this investor funds an external book (CAP1/CAP2). See PrcFilterBar's
   *  investorHasExternal, which now reads this instead of hardcoding "WAFRA". */
  isExternalFunder: boolean;
}

/**
 * One raw investor code observed in BankDwn, and what it is worth.
 *
 * The DOMAIN of a code mapping, which is why the form is a multi-select and not a text box:
 * a mapping to a code that does not exist maps nothing, silently and for ever, with no error
 * anywhere to notice — the pool simply never contains any contracts.
 */
export interface BankdwnInvestorCode {
  /** Normalised: the warehouse column is numeric and reads back as "10.0". */
  code: string;
  /**
   * DERIVED FROM THE CODE ITSELF — codes 40–49 other than 42 and 44 are Serviced. The PRC
   * population is `consider AND Owned`, so mapping a Serviced code contributes exactly zero
   * exposure to the pool, by construction. Worth knowing before choosing, not after.
   */
  ownedServiced: "Owned" | "Serviced";
  /** The code's name in the investor listing (e.g. "Unassigned / Staging Pool"). */
  mapping: string | null;
}

/**
 * What a review answers with. `needsLimits` is the auto-trigger's condition, decided
 * server-side so the browser does not re-derive "does this thing have any limits" from
 * three tables — and it is only ever true for a CREATION. A ceiling adjustment to a source
 * that already has limits needs no prompt: percentage limits recompute against the new base
 * on their own. Firing on every routine change would train people to dismiss it.
 */
export interface CapitalReviewResult {
  ok: boolean;
  error?: string;
  decision?: "APPROVED" | "REJECTED";
  applied?: boolean;
  needsLimits?: boolean;
  /** Which fact was approved, so the toast can say so rather than "capital changed". */
  changeKind?: CapitalChangeKind;
  investor?: string;
  facilityCode?: string | null;
  facilityName?: string | null;
  capitalType?: CapitalType;
}

export function useCapitalSources(
  includeSuperseded = false,
  page: { limit: number; offset: number } = { limit: 10, offset: 0 },
) {
  return useQuery({
    queryKey: ["prc-capital-sources", includeSuperseded, page],
    queryFn: () => {
      const q = new URLSearchParams({ limit: String(page.limit), offset: String(page.offset) });
      if (includeSuperseded) q.set("includeSuperseded", "true");
      return getJson<{ sources: CapitalSource[]; total: number }>(
        `${PRC_PREFIX}capital-sources?${q.toString()}`,
      );
    },
  });
}

export function useCapitalProposals(
  investor?: string,
  page: { limit: number; offset: number } = { limit: 10, offset: 0 },
) {
  return useQuery({
    queryKey: ["prc-capital-proposals", investor ?? "ALL", page],
    queryFn: () => {
      const q = new URLSearchParams({ limit: String(page.limit), offset: String(page.offset) });
      if (investor) q.set("investor", investor);
      return getJson<{ proposals: CapitalProposal[]; total: number }>(
        `${PRC_PREFIX}capital-proposals?${q.toString()}`,
      );
    },
  });
}

/**
 * The investors a proposal's investor may be. Fetched rather than hardcoded, because
 * "+ Add a new investor" would otherwise register a row the form could never select.
 *
 * PrcFilterBar's own investor options now come from this same hook (see
 * useInvestorFilterOptions in PrcFilterBar.tsx) — Monitor, Capacity and Snapshot pick up a
 * newly-registered investor the same way Manage Capital's forms already did, with the static
 * WAFRA/NAVITAS pair kept only as the loading/error fallback.
 */
export function useInvestors() {
  return useQuery({
    queryKey: ["prc-investors"],
    queryFn: () => getJson<Investor[]>(`${PRC_PREFIX}investors`),
  });
}

export function useCreateInvestor() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { displayName: string; investorCode?: string }) =>
      postJson<Investor>(`${PRC_PREFIX}investors`, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["prc-investors"] }),
  });
}

/**
 * The codes a mapping may name. Cached hard: it is a scan of the warehouse whose answer
 * changes only when a new BankDwn snapshot lands, and every open of the form would otherwise
 * re-run it.
 */
export function useInvestorCodes() {
  return useQuery({
    queryKey: ["prc-investor-codes"],
    staleTime: 30 * 60 * 1000,
    queryFn: () => getJson<BankdwnInvestorCode[]>(`${PRC_PREFIX}investor-codes`),
  });
}

/** Do two code sets say the same thing? Order and duplicates are not part of the fact. */
export function sameCodeSet(a: string[] | null, b: string[] | null): boolean {
  const norm = (xs: string[] | null) => [...new Set(xs ?? [])].sort().join(",");
  return norm(a) === norm(b);
}

/**
 * The live row a new proposal would supersede, or undefined for a genuine creation.
 *
 * Exported because the dialog needs it BEFORE submitting — picking Equity for an investor
 * that already has some must open pre-filled as an adjustment, never as a second blank
 * creation form. The backend derives the same answer independently and is the real
 * guarantee; this is what makes the form show the right thing.
 */
export function findLiveSource(
  sources: CapitalSource[],
  investor: string,
  capitalType: CapitalType,
  facilityCode?: string | null,
): CapitalSource | undefined {
  return sources.find(
    (s) =>
      s.status === "LIVE" &&
      s.capitalType === capitalType &&
      // Unbooked types are unique per investor; a debt source is unique per book.
      (UNBOOKED_TYPES.includes(capitalType)
        ? s.investor === investor
        : s.facilityCode === (facilityCode ?? null)),
  );
}

/** Every query a capital change can invalidate, in one place so none is forgotten. */
function invalidateCapital(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ["prc-capital-sources"] });
  qc.invalidateQueries({ queryKey: ["prc-capital-proposals"] });
  // A ceiling IS the denominator, so an approval moves the capacity cards...
  qc.invalidateQueries({ queryKey: ["prc-capacity"] });
  qc.invalidateQueries({ queryKey: ["prc-facility-capacity"] });
  // ...and, for Equity, every internal percentage and RAG status that was scored against
  // the old capital base.
  qc.invalidateQueries({ queryKey: ["prc-concentration"] });
  qc.invalidateQueries({ queryKey: ["prc-limits"] });
  // ...and the joints, which is the one that is easy to miss. A joint has no threshold of
  // its own: its headroom is min() over its MEMBERS' headrooms, and an internal member's
  // headroom is threshold_pct x total_capital_usd - actual. So moving the capital base moves
  // every joint built on internal limits, without any joint having been touched.
  qc.invalidateQueries({ queryKey: ["prc-joints"] });
  qc.invalidateQueries({ queryKey: ["prc-joint-defs"] });
  qc.invalidateQueries({ queryKey: ["prc-joint-candidates"] });
}

export function useProposeCapital() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CapitalProposalInput) =>
      postJson<{ proposalId: string; changeType: "ADD" | "ADJUST" }>(
        `${PRC_PREFIX}capital-proposals`,
        input,
      ),
    onSuccess: () => invalidateCapital(qc),
  });
}

export function useReviewCapital() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      proposalId: string;
      decision: "APPROVED" | "REJECTED";
      notes: string;
      reviewerRole?: string;
    }) =>
      postJson<CapitalReviewResult>(
        `${PRC_PREFIX}capital-proposals/${encodeURIComponent(input.proposalId)}/review`,
        {
          decision: input.decision,
          notes: input.notes,
          reviewerRole: input.reviewerRole,
        },
      ),
    onSuccess: () => invalidateCapital(qc),
  });
}

/**
 * Where "Set up limits now" goes: Manage limits, pre-scoped to this exact source.
 *
 * Uses the params ManageLimitsPage already reads — `?facility=` is the internal/external
 * TYPE, `?book=` is the CAP 1 / CAP 2 book, `?investor=` picks whose appetite. `?from=` is
 * the existing back-link convention, so the round trip lands back here. Arriving with
 * `?facility=` also opens the Add limit form straight away, which is the whole point of
 * "Set up limits now".
 *
 * LOWERCASE facility, deliberately. `scopeFromParam` uppercases before comparing so either
 * case sets the scope, but the deep-link effect in ManageLimitsPage compares
 * `facilityParam === "external"` literally — an uppercase value would set the scope
 * correctly and then pick the wrong limit list underneath it.
 */
export function limitsDeepLink(source: {
  investor: string;
  capitalType: CapitalType;
  facilityCode?: string | null;
}): string {
  const params = new URLSearchParams();
  params.set(
    "facility",
    UNBOOKED_TYPES.includes(source.capitalType) ? "internal" : "external",
  );
  if (source.facilityCode) params.set("book", source.facilityCode);
  params.set("investor", source.investor);
  params.set("from", "manage-capital");
  return `/risk-concentrations/manage-limits?${params.toString()}`;
}
